import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Mail, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { CONTACT } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", cruise_date: "", guests: "", enquiry_type: "General", message: "" };

const EnquiryDialog = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm({ ...empty, enquiry_type: e.detail?.enquiryType || "General" });
      setSubmitted(false);
      setOpen(true);
    };
    window.addEventListener("open-enquiry", handler);
    return () => window.removeEventListener("open-enquiry", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      setTimeout(() => { setSubmitted(false); setForm(empty); }, 200);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-lg border border-white/10 bg-vivid-base/60 px-3 py-2.5 text-white outline-none focus:border-vivid-magenta placeholder:text-white/40";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="enquiry-dialog" className="max-w-lg border-white/10 bg-vivid-surface text-white">
        {submitted ? (
          <div data-testid="enquiry-success" className="flex flex-col items-center px-2 py-6 text-center">
            <div className="relative mb-6">
              <div className="absolute inset-0 animate-ping rounded-full bg-vivid-magenta/30" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-vivid-magenta to-vivid-cyan shadow-[0_0_40px_rgba(255,0,122,0.55)]">
                <Check className="h-10 w-10 text-white" strokeWidth={3} />
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-white">Thank you!</h2>
            <p className="mt-3 max-w-sm text-white/70">
              Your enquiry is on its way to the <span className="text-white">Vivid Cruises Sydney</span> team. We will be in touch very soon about your night on Sydney Harbour.
            </p>
            <div className="mt-5 flex items-center gap-2 rounded-full border border-white/10 bg-vivid-base/50 px-4 py-2 text-sm text-white/70">
              <Sparkles className="h-4 w-4 text-vivid-cyan" />
              A confirmation has been sent to your email
            </div>
            <button
              data-testid="enquiry-success-close"
              onClick={() => handleOpenChange(false)}
              className="mt-7 rounded-full bg-vivid-magenta px-10 py-3 font-medium text-white shadow-[0_0_20px_rgba(255,0,122,0.4)] transition hover:bg-vivid-magentaHover"
            >
              Done
            </button>
          </div>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Enquire · {form.enquiry_type}</DialogTitle>
          <DialogDescription className="text-white/60">
            Send us your details and we'll reply about your Vivid Cruises Sydney harbour cruise.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="enq-name" className={field} placeholder="Full name*" value={form.name} onChange={set("name")} />
            <input data-testid="enq-email" type="email" className={field} placeholder="Email*" value={form.email} onChange={set("email")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="enq-phone" className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} />
            <input data-testid="enq-guests" className={field} placeholder="No. of guests" value={form.guests} onChange={set("guests")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="enq-type" className={field} value={form.enquiry_type} onChange={set("enquiry_type")}>
              {["General", "Private Charter", "Corporate", "Birthday", "Family", "Group Booking"].map((t) => (
                <option key={t} value={t} className="bg-vivid-surface">{t}</option>
              ))}
            </select>
            <input data-testid="enq-date" type="date" className={field} value={form.cruise_date} onChange={set("cruise_date")} />
          </div>
          <textarea data-testid="enq-message" rows={3} className={field} placeholder="Tell us about your Vivid cruise plans…" value={form.message} onChange={set("message")} />

          <button
            data-testid="enq-submit"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-vivid-magenta py-3.5 font-medium text-white shadow-[0_0_20px_rgba(255,0,122,0.4)] transition hover:bg-vivid-magentaHover disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
            Send Enquiry
          </button>
          <p className="flex items-center justify-center gap-2 text-xs text-white/50">
            <Mail className="h-3.5 w-3.5" /> {CONTACT.email}
          </p>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryDialog;
